import axios from 'axios';
import { AxiosInstance } from 'axios';
import Env from '@lib/Env';

import { IOauthClient, TAuthorizationParams } from '../types';

import AccessToken from './AccessToken';
import PostMessage from './PostMessage';

export default class OauthClient {
    accessToken: AccessToken;
    config: IOauthClient.Config;
    state: string;
    receiveMessage: EventListenerObject;

    readonly stateKey: string = 'state';
    readonly authorizeUrl: string = 'authorize';
    readonly passwordlessChannels: IOauthClient.PasswordlessChannels = {
        email: 'email_code',
        sms: 'sms_code',
    };

    constructor(config: IOauthClient.Config) {
        this.config = config;
        this.getAccessToken();
    }

    getServer(): string {
        return this.config.server;
    }

    getClientId(): string {
        return this.config.client_id;
    }

    getCurrentHost(): string {
        return window.location.protocol + '//' + window.location.host;
    }

    getAuthorizeUrl(): string {
        return `${this.getServer()}/${this.authorizeUrl}`;
    }

    buildAuthorizationUrl(state: string): string {
        return [
            this.getAuthorizeUrl(),
            `?state=${state}`,
            `&response_type=token`,
            `&approval_prompt=auto`,
            `&client_id=${this.getClientId()}`,
            `&redirect_uri=${this.getRedirectUri()}`,
        ].join('');
    }

    getRedirectUri() {
        return this.getCurrentHost() + '/' + this.config.redirect_uri.replace(/^\/|\/$/g, '');
    }

    getState() {
        return this.state;
    }

    getHttpClient(includeToken = true): AxiosInstance {
        if (includeToken && !this.hasAccessToken()) {
            console.warn('OauthClient has empty access token');
        }

        return axios.create({
            baseURL: this.getServer(),
            timeout: 30000,
            headers:
                includeToken && this.hasAccessToken()
                    ? {
                          Authorization: `Bearer ${this.getAccessTokenJwt()}`,
                      }
                    : undefined,
            proxy: this.config.proxy || undefined,
        });
    }

    getAccessToken(): AccessToken {
        if (!this.accessToken) {
            this.accessToken = AccessToken.getFromLocalStorage(this.config.token_storage_key);
        }
        return this.accessToken;
    }

    getAccessTokenJwt(): string {
        return this.accessToken && this.accessToken.getToken();
    }

    setAccessToken(accessToken: AccessToken): AccessToken {
        if (!(accessToken instanceof AccessToken)) {
            throw new Error('Provided token as not instance of AccessToken');
        }
        this.accessToken = accessToken;
        this.accessToken.saveToLocalStorage(this.config.token_storage_key);
        return this.accessToken;
    }

    hasAccessToken(): boolean {
        return !!this.accessToken && !!this.accessToken.getToken();
    }

    hasValidAccessToken(expireLeeway = 60): boolean {
        if (!this.hasAccessToken()) {
            return false;
        }
        return this.accessToken.isValid(expireLeeway);
    }

    clearAccessToken() {
        this.accessToken && this.accessToken.removeFromLocalStorage(this.config.token_storage_key);
        this.accessToken = undefined;
    }

    isSupportedPasswordlessChannel(channel: string): boolean {
        return Object.keys(this.passwordlessChannels).includes(channel);
    }

    getGrantType(channel: string): string {
        return this.passwordlessChannels[channel];
    }

    async requestPasswordlessLoginCode(recipient: string, channel: string): Promise<any> {
        if (!recipient || !channel) {
            throw new Error('Enter recipient and channel');
        }

        if (!this.isSupportedPasswordlessChannel(channel)) {
            throw new Error(`Unsupported channel: ${channel}`);
        }

        try {
            const response = await this.getHttpClient(false).post('api/passwordless/request-code', {
                client_id: this.config.client_id,
                channel,
                recipient,
            });

            return response.data;
        } catch (e) {
            Env.log.error(e);
        }
    }

    createState(): string {
        return Math.random().toString(36).substring(2);
    }

    authorization(params: TAuthorizationParams): Promise<AccessToken> {
        const state = this.createState();
        const url = this.buildAuthorizationUrl(state);

        if (params.onSetUrl !== undefined) {
            params.onSetUrl(url);
        }

        return new Promise((resolve, reject) => {
            if (this.receiveMessage) {
                window.removeEventListener('message', this.receiveMessage);
            }

            this.receiveMessage = {
                handleEvent: (message: MessageEvent): void => {
                    try {
                        const postMessage = new PostMessage(message);

                        if (!postMessage.isFromAuthServer(this) || postMessage.payload.state !== state) {
                            return;
                        }

                        if (postMessage.action == 'auth.error') {
                            throw new Error('error');
                        }

                        if (postMessage.action == 'auth.login') {
                            return params.silent ? reject('need_login') : null;
                        }

                        const accessToken = new AccessToken(postMessage.payload.access_token);

                        this.setAccessToken(accessToken);

                        if (params.onClose !== undefined) {
                            params.onClose();
                        }

                        window.removeEventListener('message', this.receiveMessage);

                        resolve(this.getAccessToken());
                    } catch (error) {
                        console.error(error, message);
                        if (params.onClose !== undefined) {
                            params.onClose();
                        }
                        window.removeEventListener('message', this.receiveMessage);
                        reject(error);
                    }
                },
            };

            window.addEventListener('message', this.receiveMessage, false);
        });
    }

    silentAuthorization(): Promise<AccessToken> {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('style', 'display:none');

        const body = document.querySelector('body');

        return this.authorization({
            silent: true,
            onSetUrl: (url: string) => {
                iframe.setAttribute('src', url);
                body.appendChild(iframe);
            },
            onClose: () => {
                body.removeChild(iframe);
            },
        });
    }

    openImplicitWindow(): Promise<AccessToken> {
        let newWindow: Window;

        return this.authorization({
            silent: false,
            onSetUrl: (url: string) => {
                newWindow = window.open(url, 'Login', 'width=800,height=600');

                window.onbeforeunload = () => {
                    newWindow.close();
                };

                newWindow.onbeforeunload = () => {
                    window.removeEventListener('message', this.receiveMessage);
                };
            },
            onClose: () => {
                newWindow.close();
            },
        });
    }

    silentLogout(): Promise<null> {
        return new Promise((resolve, reject) => {
            const iframe = document.createElement('iframe');

            const state = this.createState();
            const url = `${this.config.server}/logout?source=modal&state=${state}`;

            iframe.setAttribute('src', url);
            iframe.setAttribute('style', 'display:none');

            const body = document.querySelector('body');

            body.appendChild(iframe);

            if (this.receiveMessage) {
                window.removeEventListener('message', this.receiveMessage);
            }

            this.receiveMessage = {
                handleEvent: (message: MessageEvent): void => {
                    try {
                        const postMessage = new PostMessage(message);

                        if (!postMessage.isFromAuthServer(this) || postMessage.payload.state !== state) {
                            return;
                        }

                        if (postMessage.action == 'auth.error' || postMessage.action != 'auth.logout') {
                            throw new Error('error');
                        }

                        window.removeEventListener('message', this.receiveMessage);
                        body.removeChild(iframe);

                        resolve();
                    } catch (error) {
                        console.error(error, message);
                        window.removeEventListener('message', this.receiveMessage);
                        reject(error);
                    }
                },
            };

            window.addEventListener('message', this.receiveMessage, false);
        });
    }

    checkScope(...reqScope: string[]) {
        if (!this.accessToken) {
            return false;
        }
        const curScope = this.accessToken.getScopeArray();
        const intersection = curScope.filter((scope) => reqScope.includes(scope));

        for (let i in reqScope) {
            if (reqScope[i] !== intersection[i]) {
                return false;
            }
        }
        return true;
    }
}
